<script lang="ts" context="module">

export const path = writable("/");

</script>

<script lang="ts">
import { onMount } from "svelte";
import { writable } from "svelte/store";
import { findResult, find, deleteOne, string2date } from "../api";
import type { FindResult } from "../api";
import Loading from "../components/loading/Loading.svelte";
import Table from "../components/table/Table.svelte";
import LocalSort from "../components/table/LocalSort.svelte";

import { fileSize, fileDate } from "../tools";
import { MediaServerUrl } from "ts-media-server-url";
import { activePage } from "../router";
import { fromMSU } from "../components/playground/store";

const data          = writable(findResult());
const isLoading     = writable(false);
const isPathInvalid = writable(false);
const past          = writable([]);
const future        = writable([]);

async function getData (path: string) {
	$isLoading = true;

	try {
		$data = await find(
			"/browse",
			{ path },
			{ stringToDate: ["atime", "mtime", "ctime"] }
		);
		$isPathInvalid = false;
	} catch (e) {
		$isPathInvalid = true;
	}

	$isLoading = false;
}

const reIsUrl      = /^https?:\/\/[a-z0-9\-_.]+\//i;
const reIsOriginal = /\/original$/;
const reIsFile     = /\/[a-z0-9\-_=]+\.[a-z0-9]+$/i;

function go (newPath: string, skipPast = false, keepFuture = false) {
	/** Convert media server url to path */
	if (reIsUrl.test(newPath)) {
		newPath = newPath.replace(reIsUrl, "/");
	}
	/** Convert original file to containing dir */
	if (reIsOriginal.test(newPath)) {
		newPath = newPath.replace(reIsOriginal, "");
	}
	/** Convert file to containing dir */
	if (reIsFile.test(newPath)) {
		newPath = newPath.replace(reIsFile, "");
	}
	/** Prepend "/" */
	if (!newPath.startsWith("/")) {
		newPath = "/"+newPath;
	}
	/** Remove trailing "/" */
	if (newPath !== "/" && newPath.endsWith("/")) {
		newPath = newPath.replace(/\/$/, "");
	}
	/** Add to history */
	if (!skipPast) {
		$past = [...$past, $path];
	}
	/** Remove future */
	if (!keepFuture) {
		$future = [];
	}

	$path = newPath;
}

function back (e?: Event) {
	if (e) { e.preventDefault(); }
	let [ next, ...rest ] = $past.reverse();

	$future = [ ...$future, $path ];
	$past   = rest.reverse();

	go(next, true, true);
}

function forward (e?: Event) {
	if (e) { e.preventDefault(); }
	let [ next, ...rest ] = $future.reverse();

	$past = [ ...$past, $path ];
	$future = rest.reverse();
	
	go(next, true, true);
}

function up (e?: Event) {
	if (e) { e.preventDefault(); }
	let newPath = $path.replace(/\/[a-zA-Z0-9\-_=]+$/, "") || "/";
	go(newPath);
}

function reload (e?: Event) {
	if (e) { e.preventDefault(); }
	getData($path);
}

function home (e?: Event) {
	if (e) { e.preventDefault(); }
	go("/");
}

function selectAll (e: Event) {
	(e.target as HTMLInputElement).select();
}

const isLoadingBatch = writable(false);

let loadingBatchI     = 0;
let loadingBatchTotal = 0;

async function confirmDeleteAllVersions ( ) {
	const toDelete = $data.result.filter((item: any) => {
		return item.isFile && item.name !== "original";
	});
	loadingBatchTotal = toDelete.length;
	
	if (confirm(`Are you sure you want to delete ALL ${loadingBatchTotal} versions? (Does not delete original)`)) {
		$isLoadingBatch = true;
		let i = 0;
		for (i = 0; i < toDelete.length; i++) {
			loadingBatchI = i + 1;
			await deleteOne((toDelete[i] as any).path);
		}
		$isLoadingBatch = false;
		reload();
	}
}

async function confirmDelete (message: string, path: string, dirUp: boolean = false) {
	if (confirm(message)) {
		$isLoading = true;
		await deleteOne(path);
		if (dirUp) {
			up();
		} else {
			reload();
		}
	}
}

function openPlayground (item: any) {
	let msu = new MediaServerUrl();
	msu.parse(item.path);
	fromMSU(msu);
	
	$activePage = "playground";
}

onMount(async ( ) => {
	await getData($path);
});

$: getData($path);

</script>

<div class="browser">
	<div class="row bg-secondary">
		<table class="table table-dark bg-secondary mb-0">
			<tbody>
				<tr>
					<td width="250">
						<button class="btn btn-secondary" on:click="{back}" disabled="{$isLoading || !$past.length}"><i class="fas fa-arrow-left"></i></button>
						<button class="btn btn-secondary" on:click="{forward}" disabled="{$isLoading || !$future.length}"><i class="fas fa-arrow-right"></i></button>
						<button class="btn btn-secondary" on:click="{up}" disabled="{$isLoading || $path === "/"}"><i class="fas fa-arrow-up"></i></button>
						<button class="btn btn-secondary" on:click="{reload}" disabled="{$isLoading}"><i class="fas fa-sync"></i></button>
						<button class="btn btn-secondary" on:click="{home}" disabled="{$isLoading || $path === "/"}"><i class="fas fa-home"></i></button>
					</td>
					<td>
						<input type="text" class="form-control" class:is-invalid="{$isPathInvalid}" value="{$path}" on:change="{(e) => { go(e.target['value']); }}" on:focus="{selectAll}" disabled="{$isLoading}">
					</td>
				</tr>
			</tbody>
		</table>
	</div>
	<div class="row position-relative">
		<LocalSort data="{$data.result}" sortKey="name" sortDir="asc" let:sorted let:sortBy let:sortKey let:sortDir>
			<Table data="{sorted}" class="table table-fixed table-dark">
				<tr slot="thead">
					<th width="40%" class="th-pointer" on:click="{( ) => sortBy("name")}">
						Name
						{#if sortKey === "name"}
							{#if sortDir === "asc"}⬇{:else}⬆{/if}
						{/if}
					</th>
					<th width="10%" class="th-pointer" on:click="{( ) => sortBy("mimetype")}">
						Type
						{#if sortKey === "mimetype"}
							{#if sortDir === "asc"}⬇{:else}⬆{/if}
						{/if}
					</th>
					<th width="10%" class="th-pointer" on:click="{( ) => sortBy("size")}">
						Size
						{#if sortKey === "size"}
							{#if sortDir === "asc"}⬇{:else}⬆{/if}
						{/if}
					</th>
					<th width="15%" class="th-pointer" on:click="{( ) => sortBy("ctime")}">
						Created
						{#if sortKey === "ctime"}
							{#if sortDir === "asc"}⬇{:else}⬆{/if}
						{/if}
					</th>
					<th width="10%">Details</th>
					<th width="15%">Actions</th>
				</tr>
				<tr slot="tbody" let:item>
					{#if item.isDir}
						<td class="dir td-pointer" on:click="{( ) => go(item.path)}">
							<i class="fas fa-folder"></i>
							&nbsp;
							{item.name}
							<br>
							{#if item.description.startsWith("http")}
								<em title="{item.description}"><a href="{item.description}" target="_blank" on:click|stopPropagation>{item.description}</a></em>
							{:else}
								<em title="{item.description}">{item.description}</em>
							{/if}
						</td>
						<td>
							{item.mimetype}
						</td>
						<td>
							{fileSize(item.size)}
						</td>
						<td>
							{fileDate(item.ctime)}
						</td>
						<td></td>
						<td>
							{#if $path !== "/"}
								<a class="text-danger" href="#delete" on:click|preventDefault="{( ) => confirmDelete(`Are you sure you want to delete this directory?`, item.path) }"><strong><i class="fas fa-times"></i> Delete directory</strong></a>
							{/if}
						</td>
					{:else if item.isFile}
						<td>
							{#if item.mimetype.startsWith("image")}
								<i class="fas fa-image"></i>
							{:else if item.mimetype.endsWith("pdf")}
								<i class="fas fa-file-pdf"></i>
							{:else}
								<i class="fas fa-file"></i>
							{/if}
							&nbsp;
							{#if item.name === "original"}
								{#if item.mimetype.startsWith("image")}
									<a href="https://media02.ogonline.nl{item.path}" target="_blank">{item.name}</a>
									<em>(
										<a href="https://media02.ogonline.nl{item.path}.jpg" target="_blank">jpg</a>
										|
										<a href="https://media02.ogonline.nl{item.path}.webp" target="_blank">webp</a>
									)</em>
								{:else}
									<a href="https://media02.ogonline.nl{item.path}" target="_blank">{item.name}</a>
								{/if}
							{:else}
								<a href="https://media02.ogonline.nl{item.path}" target="_blank">{item.name}</a>
							{/if}
							<br>
							<em title="{item.description}">{item.description}</em>
						</td>
						<td>
							{item.mimetype}
						</td>
						<td>
							{fileSize(item.size)}
						</td>
						<td>
							{fileDate(item.ctime)}
						</td>
						<td>
							{#if item.width && item.height}
								{item.width} &times; {item.height}
							{/if}
						</td>
						<td>
							{#if item.name === "original"}
							<a class="text-danger" href="#delete" on:click|preventDefault="{( ) => confirmDelete(`Are you sure you want to delete this directory?`, item.path, true) }"><strong><i class="fas fa-times"></i> Delete directory</strong></a>
							&nbsp;
							<a class="text-primary" href="#playground" on:click|preventDefault="{( ) => openPlayground(item) }"><strong><i class="fas fa-gamepad"></i> Playground</strong></a>
							{:else}
							<a class="text-danger" href="#delete" on:click|preventDefault="{( ) => confirmDelete(`Are you sure you want to delete this version?`, item.path) }"><strong><i class="fas fa-times"></i> Delete version</strong></a>
							&nbsp;
							<a class="text-warning" href="#delete" on:click|preventDefault="{( ) => confirmDeleteAllVersions() }"><strong><i class="fas fa-times"></i> Delete all versions</strong></a>
							{/if}
						</td>
					{/if}
				</tr>
			</Table>
		</LocalSort>
		<Loading class="bg-dark" isLoading={$isLoading} duration="{25}"><div class="loading-spinner"></div></Loading>
		<Loading class="bg-dark" isLoading={$isLoadingBatch} duration="{25}"><div class="loading-spinner"></div><p class="mb-0 text-center">{loadingBatchI} of {loadingBatchTotal}</p></Loading>
	</div>
</div>
